import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

function Navbar() {
  const { i18n, t } = useTranslation();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const langRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (langRef.current && !langRef.current.contains(event.target)) {
        setLangMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Nav links using i18n translations
  const navLinks = [
    { to: "/", label: t("nav.home") },
    { to: "/about", label: t("nav.about") },
    { to: "/products", label: t("nav.products") },
    { to: "/testimonials", label: t("nav.testimonials") },
    { to: "/faq", label: t("nav.faq") },
    { to: "/contact", label: t("nav.contact") }
  ];

  // Flags completely removed from the dataset
  const languages = [
    { code: "en", name: "English" },
    { code: "fr", name: "Français" },
    { code: "es", name: "Español" },
    { code: "pt", name: "Português" },
    { code: "hi", name: "हिंदी" }
  ];

  // Extracts the core language token (handles cases like 'en-US' gracefully)
  const baseLangCode = i18n.language?.split("-")[0] || "en";
  const currentLang = languages.find(lang => lang.code === baseLangCode) || languages[0];

  const changeLanguage = (code) => {
    i18n.changeLanguage(code);
    setLangMenuOpen(false);
  };

  return (
    <nav className={`navbar ${scrolled ? "scrolled" : ""}`}>
      <Link to="/" className="logo-container" onClick={() => setMenuOpen(false)}>
        <img src="/images/logo.png" alt="Chirayu Medicos" className="logo-img" />
        <div className="logo-text">
          <span className="logo-main">CHIRAYU MEDICOS</span>
          <span className="logo-sub">Private Limited</span>
        </div>
      </Link>

      <div className="nav-controls">
        <div className="language-switcher" ref={langRef}>
          <button 
            className="lang-trigger"
            onClick={() => setLangMenuOpen(!langMenuOpen)}
            aria-label="Select Language"
          >
            <svg className="globe-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="2" y1="12" x2="22" y2="12"/>
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
            </svg>
            
            <span className="current-lang">{currentLang.name}</span>

            <svg className={`chevron ${langMenuOpen ? 'open' : ''}`} width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
              <path d="M2 4l4 4 4-4"/>
            </svg>
          </button>
          
          {langMenuOpen && (
            <div className="lang-dropdown">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  className={`lang-option ${currentLang.code === lang.code ? 'active' : ''}`}
                  onClick={() => changeLanguage(lang.code)}
                >
                  {/* Clean text-only display without flag nodes */}
                  <span className="lang-name">{lang.name}</span>
                  {currentLang.code === lang.code && (
                    <svg className="check-icon" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M13.5 3.5L6 11 2.5 7.5"/>
                    </svg>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        <button className="hamburger" onClick={() => setMenuOpen(!menuOpen)}>
          <span className={menuOpen ? "open" : ""}></span>
          <span className={menuOpen ? "open" : ""}></span>
          <span className={menuOpen ? "open" : ""}></span>
        </button>
      </div>

      <div className={`nav-links ${menuOpen ? "open" : ""}`}>
        {navLinks.map((link) => {
          // RESTORED: Custom two-line block that safely renders "(Terms & Policies)" on line two
          if (link.to === "/about") {
            return (
              <Link
                key={link.to}
                to={link.to}
                className={`nav-link ${location.pathname === link.to ? "active" : ""}`}
                onClick={() => setMenuOpen(false)}
                style={{
                  display: "inline-flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  lineHeight: "1.2",
                  verticalAlign: "middle",
                  paddingTop: "4px",
                  paddingBottom: "4px"
                }}
              >
                <span>{link.label}</span>
                <span style={{ fontSize: "0.72rem", fontWeight: "normal", opacity: 0.9 }}>
                  (Terms & Policies)
                </span>
              </Link>
            );
          }

          // Default structure for standard single-line elements
          return (
            <Link
              key={link.to}
              to={link.to}
              className={`nav-link ${location.pathname === link.to ? "active" : ""}`}
              onClick={() => setMenuOpen(false)}
            >
              {link.label}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}

export default Navbar;